'use client'

import {
  Border1,
  ColorBaseTransparent,
  ColorBaseWhite,
  ColorTextDark,
  ColorTextLight,
  SizeBorder1,
  SizeRadiusRounded,
  SizeSpacing3,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import {
  Paper,
  PaperElevations,
} from '@snowball-tech/fractal/dist/src/components/Paper'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { Themes } from '@snowball-tech/fractal/dist/src/constants'

import { type ForwardedRef, forwardRef } from 'react'

import isEmpty from 'lodash/fp/isEmpty'

import { Apps } from '../../enums'
import { getSigninUrl, getUrl, usePathname } from '../../helpers/navigation'
import useTranslations from '../../hooks/useTranslations'
import useUrls from '../../hooks/useUrls'
import { asPx, spacings } from '../../styles/helpers'
import LegacyTable from '../LegacyTable'

export const TYPE = 'paywall'

type Props = {
  editionUuid?: string
  forHtml?: boolean
  openInNewTab?: boolean
  withSignin?: boolean
}
const Paywall = forwardRef<HTMLElement, Props>(
  (
    {
      editionUuid,
      forHtml = false,
      openInNewTab = false,
      withSignin = false,
    }: Props,
    _ref: ForwardedRef<HTMLElement>,
  ) => {
    const pathname = usePathname()
    const $t = useTranslations()

    const URLs = useUrls()
    const currentUrl =
      typeof window === 'undefined'
        ? `${URLs.media}${pathname}`
        : window.location.href

    const title = (
      <>
        <Typography
          element="p"
          inlineStyle={forHtml}
          style={{ color: ColorTextLight }}
          variant="heading-1"
        >
          ✨
        </Typography>

        <Typography
          element="p"
          inlineStyle={forHtml}
          style={{ color: ColorTextLight }}
          variant="heading-4"
        >
          {$t('edition.paywall.title')}
        </Typography>
      </>
    )

    const paddingVertical = spacings[1] - spacings.quarter
    const minHeight =
      spacings[6] - paddingVertical * 2 - (asPx(SizeBorder1) as number) * 2

    const subscribeButton = (
      <Button
        fullStyle={forHtml}
        href={getUrl(
          Apps.Media,
          '/snowball-plus',
          isEmpty(editionUuid)
            ? 'origin=paywall'
            : `from=${editionUuid}&source=${forHtml ? 'inbox' : 'media'}&origin=paywall`,
        )}
        inlineStyle={forHtml}
        label={$t('edition.paywall.subscribe.label')}
        style={
          forHtml
            ? {
                backgroundColor: ColorBaseTransparent,
                border: 'none',
                boxShadow: 'none',
                color: ColorTextDark,
                display: 'block',
                margin: '0 auto',
                maxWidth: '100%',
                minHeight: asPx(minHeight, { withUnit: true }),
                padding: 0,
                pointerEvents: 'auto',
              }
            : undefined
        }
        target={openInNewTab ? '_blank' : undefined}
        theme={Themes.Dark}
        variant="display"
        wrapperStyles={
          forHtml
            ? {
                marginTop: 0,
              }
            : undefined
        }
      >
        <Typography
          inlineStyle={forHtml}
          style={
            forHtml
              ? {
                  color: ColorTextDark,
                  lineHeight: asPx(minHeight, { withUnit: true }),
                  maxWidth: '100%',
                  minHeight: asPx(minHeight, { withUnit: true }),
                  whiteSpace: 'break-spaces',
                }
              : undefined
          }
          variant="body-1-median"
        >
          {$t('take_action.cta.label')}
        </Typography>
      </Button>
    )

    return forHtml ? (
      <LegacyTable
        id={TYPE}
        col
        data-type={TYPE}
        style={{
          marginBottom: asPx(SizeSpacing3, { withUnit: true }),
        }}
      >
        {/* Transparent background */}

        <LegacyTable
          col
          defaultCellStyle={{ textAlign: 'center' }}
          elevation={PaperElevations.Higher}
          gap={spacings[3]}
          style={{ padding: asPx(SizeSpacing3, { withUnit: true }) }}
          theme={Themes.Dark}
        >
          <LegacyTable
            col
            gap={spacings[1]}
            style={{ backgroundColor: ColorBaseTransparent }}
          >
            {title}
          </LegacyTable>

          <LegacyTable
            autoWidth
            center
            defaultCellStyle={{
              backgroundColor: ColorBaseWhite,
              border: Border1,
              borderRadius: SizeRadiusRounded,
              borderWidth: asPx(SizeBorder1, { withUnit: true }),
              margin: '0 auto',
              maxWidth: '100%',
              padding: `${asPx(paddingVertical, { withUnit: true })} ${asPx(
                spacings[3] - spacings.quarter,
                { withUnit: true },
              )}`,
            }}
          >
            {subscribeButton}
          </LegacyTable>
        </LegacyTable>
      </LegacyTable>
    ) : (
      <div className="w-full">
        <div id={TYPE} className="relative">
          <div
            className="z absolute -top-10 left-0 h-10
      w-full bg-body-light [background:linear-gradient(180deg,rgba(255,251,244,0)_0%,rgba(255,251,244,0.75)_25%,var(--color-background-body-light)_100%)]"
          />

          <Paper
            className="flex flex-col items-center gap-3 p-3 text-light"
            elevation={PaperElevations.Higher}
            theme={Themes.Dark}
          >
            <div className="flex flex-col items-center gap-1">{title}</div>

            {subscribeButton}

            {withSignin && (
              <div className="flex w-full max-w-[480px] flex-col items-center">
                <div>
                  <Typography element="span" variant="caption-median">
                    {$t('edition.paywall.already_subscribed')}
                  </Typography>
                  &nbsp;
                  <Typography
                    className="inline-block text-light"
                    href={getSigninUrl(Apps.Media, currentUrl)}
                    target={openInNewTab ? '_blank' : undefined}
                    variant="caption-link"
                  >
                    {$t('edition.paywall.signin.label')}
                  </Typography>
                </div>
              </div>
            )}
          </Paper>
        </div>
      </div>
    )
  },
)
Paywall.displayName = 'Paywall'

export default Paywall
